// FILE: components/sections/TableSection.tsx
// This section documents the data table component.

import React, { useState, useMemo, useEffect } from 'react';
import { 
    SectionContainer, 
    ComponentPreview,
    SubSection, 
    Badge, 
    Button,
    ButtonGroup,
    PropsTable,
    Card,
    Pagination,
    EmptyState,
    ToggleSwitch,
    Slider,
    BrowserCompatibility
} from '../Content';
import { Table } from '../Table';
import { VirtualizedTable } from '../VirtualizedTable';
import { Icon } from '../icons';
import { PropDef } from '../../../types';
import CodeBlock from '../CodeBlock';

// Sample data
const simpleTableData = [
    { id: 'p1', name: 'Magic Widget', sku: 'MW-001', status: 'In Stock', statusVariant: 'success' as const },
    { id: 'p2', name: 'Gizmo Pro', sku: 'GP-003', status: 'Out of Stock', statusVariant: 'error' as const },
    { id: 'p3', name: 'Thingamajig', sku: 'TM-002', status: 'Backordered', statusVariant: 'warning' as const },
];

const interactiveTableData = [
    { id: 'inv-001', customer: 'Acme Inc.', date: '2023-10-26', amount: '$2,500.00', status: 'Paid', statusVariant: 'success' as const },
    { id: 'inv-002', customer: 'Stark Industries', date: '2023-10-25', amount: '$1,500.75', status: 'Pending', statusVariant: 'warning' as const },
    { id: 'inv-003', customer: 'Wayne Enterprises', date: '2023-10-24', amount: '$800.00', status: 'Paid', statusVariant: 'success' as const },
    { id: 'inv-004', customer: 'Ollivanders', date: '2023-10-22', amount: '$300.50', status: 'Overdue', statusVariant: 'error' as const },
    { id: 'inv-005', customer: 'Cyberdyne Systems', date: '2023-10-21', amount: '$12,000.00', status: 'Draft', statusVariant: 'default' as const },
];

const largeTableData = Array.from({ length: 1000 }, (_, i) => ({
    id: `item-${String(i + 1).padStart(4, '0')}`,
    name: `Product ${i + 1}`,
    category: ['Electronics', 'Clothing', 'Books', 'Home', 'Sports'][i % 5],
    price: `$${(Math.random() * 1000 + 10).toFixed(2)}`,
    stock: Math.floor(Math.random() * 1000),
    status: ['In Stock', 'Low Stock', 'Out of Stock'][i % 3],
    lastUpdated: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
}));

// Column configurations
const virtualizedTableColumns = [
    { key: 'id', header: 'ID', sortable: true, width: 120, textWrap: 'nowrap' as const },
    { key: 'name', header: 'Product Name', sortable: true, width: 250, textWrap: 'ellipsis' as const, className: 'font-medium' },
    { key: 'category', header: 'Category', sortable: true, width: 140, textWrap: 'nowrap' as const },
    { key: 'price', header: 'Price', sortable: true, width: 120, textWrap: 'nowrap' as const, className: 'text-right font-mono' },
    { key: 'stock', header: 'Stock', sortable: true, width: 100, textWrap: 'nowrap' as const, className: 'text-center' },
    { key: 'status', header: 'Status', sortable: true, width: 140, textWrap: 'nowrap' as const },
    { key: 'lastUpdated', header: 'Last Updated', sortable: true, width: 160, textWrap: 'nowrap' as const }
];

// Props definitions
const tableRootProps: PropDef[] = [
    { name: 'striped', type: 'boolean', default: 'false', description: 'Adds zebra-striping to table rows for readability.' },
    { name: 'hoverable', type: 'boolean', default: 'false', description: 'Adds a hover effect to table rows.' },
    { name: 'bordered', type: 'boolean', default: 'true', description: 'Global border control for the entire table component.' },
];

const tableRowProps: PropDef[] = [
    { name: 'selected', type: 'boolean', default: 'false', description: 'Highlights the row with selection styling.' },
    { name: 'highlighted', type: 'boolean', default: 'false', description: 'Highlights the row with the specified highlight color.' },
    { name: 'highlightColor', type: '"primary" | "secondary" | "success" | "warning" | "error" | "info"', default: '"primary"', description: 'Color theme for row highlighting.' },
];

const tableCellProps: PropDef[] = [
    { name: 'highlighted', type: 'boolean', default: 'false', description: 'Highlights the cell with the specified highlight color.' },
    { name: 'highlightColor', type: '"primary" | "secondary" | "success" | "warning" | "error" | "info"', default: '"primary"', description: 'Color theme for cell highlighting.' },
];

const virtualizedTableProps: PropDef[] = [
    { name: 'data', type: 'T[]', default: 'required', description: 'Array of data objects to display in the table.' },
    { name: 'columns', type: 'ColumnConfig[]', default: 'required', description: 'Configuration for table columns.' },
    { name: 'height', type: 'number', default: 'required', description: 'Fixed height of the table container for virtualization.' },
    { name: 'rowHeight', type: 'number', default: 'required', description: 'Height of each individual row in pixels.' },
    { name: 'showHeader', type: 'boolean', default: 'true', description: 'Controls visibility of the header section.' },
    { name: 'showFooter', type: 'boolean', default: 'true', description: 'Controls visibility of the footer section.' },
    { name: 'bordered', type: 'boolean', default: 'true', description: 'Global border control for the entire table component.' },
    { name: 'searchable', type: 'boolean', default: 'false', description: 'Enables search functionality across all columns.' },
    { name: 'filterable', type: 'boolean', default: 'false', description: 'Enables filtering options for the table data.' },
    { name: 'selectable', type: 'boolean', default: 'false', description: 'Enables row selection with checkboxes.' },
    { name: 'striped', type: 'boolean', default: 'true', description: 'Adds zebra-striping to table rows for better readability.' },
    { name: 'hoverable', type: 'boolean', default: 'true', description: 'Adds hover effects to table rows.' },
    { name: 'multiSort', type: 'boolean', default: 'false', description: 'Enables multi-column sorting with up to 3 priority levels.' },
    { name: 'sortable', type: 'boolean', default: 'true', description: 'Global toggle to enable/disable sorting functionality.' },
    { name: 'highlightedRows', type: '(string | number)[]', default: '[]', description: 'Array of row IDs to highlight with the specified color.' },
    { name: 'highlightedColumns', type: 'string[]', default: '[]', description: 'Array of column keys to highlight with the specified color.' },
    { name: 'highlightColor', type: '"primary" | "secondary" | "success" | "warning" | "error" | "info"', default: '"primary"', description: 'Default highlight color for rows and columns.' },
];

const columnConfigProps: PropDef[] = [
    { name: 'key', type: 'any', default: 'required', description: 'Unique identifier for the column, used to access data values.' },
    { name: 'header', type: 'string', default: 'required', description: 'Display text for the column header.' },
    { name: 'sortable', type: 'boolean', default: 'false', description: 'Whether the column can be sorted by clicking the header.' },
    { name: 'width', type: 'number', default: 'auto', description: 'Fixed width of the column in pixels.' },
    { name: 'textWrap', type: '"wrap" | "nowrap" | "ellipsis"', default: '"ellipsis"', description: 'Text wrapping behavior.' },
    { name: 'className', type: 'string', default: '""', description: 'Custom CSS classes to apply to the column cells.' },
];

type SortConfig = {
    key: keyof typeof interactiveTableData[0] | null;
    direction: 'ascending' | 'descending';
    priority: number;
};

const SortableHeader: React.FC<{
    children: React.ReactNode;
    sortKey: keyof typeof interactiveTableData[0];
    sortConfigs: SortConfig[];
    onSort: (key: keyof typeof interactiveTableData[0]) => void;
    multiSort?: boolean;
}> = ({ children, sortKey, sortConfigs, onSort, multiSort = false }) => {
    const sortConfig = sortConfigs.find(config => config.key === sortKey);
    const isSorted = !!sortConfig;
    const directionIcon = sortConfig?.direction === 'ascending' ? 'arrow_upward' : 'arrow_downward';

    return (
        <Table.HeadCell>
            <button className="flex items-center gap-2 font-bold group" onClick={() => onSort(sortKey)}>
                {children}
                {isSorted ? (
                    <div className="flex items-center gap-1">
                        <Icon className="!text-base text-brand-navy dark:text-dark-text-primary">
                            {directionIcon}
                        </Icon>
                        {multiSort && sortConfig.priority > 1 && (
                            <span className="text-xs text-brand-navy dark:text-dark-text-primary font-bold">
                                {sortConfig.priority}
                            </span>
                        )}
                    </div>
                ) : (
                    <Icon className="!text-base opacity-0 group-hover:opacity-50 transition-opacity">unfold_more</Icon>
                )}
            </button>
        </Table.HeadCell>
    );
};

export const TableSection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({groupTitle, sectionTitle, id}) => {
    // State for workbench
    const [tableType, setTableType] = useState<'regular' | 'virtualized'>('regular');
    const [isStriped, setIsStriped] = useState(true);
    const [isHoverable, setIsHoverable] = useState(true);
    const [isSelectable, setIsSelectable] = useState(true);
    const [isSearchable, setIsSearchable] = useState(false);
    const [isFilterable, setIsFilterable] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [tableHeight, setTableHeight] = useState(400);
    const [rowHeight, setRowHeight] = useState(50);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [isMultiSort, setIsMultiSort] = useState(false);
    const [isSortable, setIsSortable] = useState(true);
    const [defaultSortPriority, setDefaultSortPriority] = useState('none');
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>(['inv-002']);
    const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([{ key: 'date', direction: 'descending', priority: 1 }]);
    const [filterQuery, setFilterQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    // Highlighting state
    const [highlightedRows, setHighlightedRows] = useState<string[]>(['inv-002']);
    const [highlightedColumns, setHighlightedColumns] = useState<string[]>(['status']);
    const [highlightColor, setHighlightColor] = useState<'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'>('primary');

    // Apply default sort when priority changes
    useEffect(() => {
        if (defaultSortPriority !== 'none') {
            setSortConfigs([{ key: defaultSortPriority as keyof typeof interactiveTableData[0], direction: 'ascending', priority: 1 }]);
        } else {
            setSortConfigs([]);
        }
    }, [defaultSortPriority]);
    
    const requestSort = (key: keyof typeof interactiveTableData[0]) => {
        if (!isSortable) return;
        
        let newSortConfigs: SortConfig[];
        
        if (isMultiSort) {
            const existingIndex = sortConfigs.findIndex(config => config.key === key);
            
            if (existingIndex >= 0) {
                newSortConfigs = [...sortConfigs];
                if (sortConfigs[existingIndex].direction === 'ascending') {
                    newSortConfigs.splice(existingIndex, 1);
                } else {
                    newSortConfigs[existingIndex] = { ...newSortConfigs[existingIndex], direction: 'descending' };
                }
            } else {
                const nextPriority = sortConfigs.length + 1;
                if (nextPriority <= 3) {
                    newSortConfigs = [...sortConfigs, { key, direction: 'ascending', priority: nextPriority }];
                } else {
                    newSortConfigs = [...sortConfigs.slice(1), { key, direction: 'ascending', priority: 3 }];
                }
            }
        } else {
            newSortConfigs = [{ key, direction: 'ascending', priority: 1 }];
        }
        
        setSortConfigs(newSortConfigs);
    };

    // Process data for regular table
    const processedData = useMemo(() => {
        let filtered = interactiveTableData;
        
        if (filterQuery) {
            filtered = filtered.filter(invoice => 
                invoice.customer.toLowerCase().includes(filterQuery.toLowerCase()) ||
                invoice.id.toLowerCase().includes(filterQuery.toLowerCase()) ||
                invoice.status.toLowerCase().includes(filterQuery.toLowerCase())
            );
        }
        
        if (sortConfigs.length > 0) {
            filtered.sort((a, b) => {
                for (const sortConfig of sortConfigs.sort((x, y) => x.priority - y.priority)) {
                    const valA = a[sortConfig.key as keyof typeof a];
                    const valB = b[sortConfig.key as keyof typeof b];
                    
                    let comparison = 0;
                    if (typeof valA === 'string' && typeof valB === 'string') {
                        if (sortConfig.key === 'date') {
                            comparison = new Date(valA).getTime() - new Date(valB).getTime();
                        } else if (sortConfig.key === 'amount') {
                            comparison = parseFloat(valA.replace(/[$,]/g, '')) - parseFloat(valB.replace(/[$,]/g, ''));
                        } else {
                            comparison = valA.localeCompare(valB);
                        }
                    }
                    
                    if (sortConfig.direction === 'descending') {
                        comparison = -comparison;
                    }
                    
                    if (comparison !== 0) {
                        return comparison;
                    }
                }
                return 0;
            });
        }
        return filtered;
    }, [interactiveTableData, filterQuery, sortConfigs]);

    // Pagination logic
    const totalPages = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    
    // Selection logic
    const isPageAllSelected = paginatedData.length > 0 && paginatedData.every(invoice => selectedInvoices.includes(invoice.id));
    const isPageSomeSelected = paginatedData.some(invoice => selectedInvoices.includes(invoice.id)) && !isPageAllSelected;

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        if (checked) {
            const newSelected = [...new Set([...selectedInvoices, ...paginatedData.map(invoice => invoice.id)])];
            setSelectedInvoices(newSelected);
        } else {
            const newSelected = selectedInvoices.filter(id => !paginatedData.some(invoice => invoice.id === id));
            setSelectedInvoices(newSelected);
        }
    };

    const handleSelectOne = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        if (e.target.checked) {
            setSelectedInvoices([...selectedInvoices, id]);
        } else {
            setSelectedInvoices(selectedInvoices.filter(selectedId => selectedId !== id));
        }
    };

    return (
        <SectionContainer id={id}>
            <div className="space-y-8">
                {/* Introduction */}
                <div>
                    <h2 className="section-title">Data Tables</h2>
                    <p className="section-subtitle">
                        Powerful, flexible table components for displaying and interacting with structured data. 
                        Choose between standard tables for simple data and virtualized tables for large datasets with advanced features.
                    </p>
                </div>

                {/* Table Structure & Architecture */}
                <SubSection title="Table Structure & Architecture">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                       Both Table and VirtualizedTable components follow a clear, intuitive structure that makes it easy for developers to customize and extend:
                    </p>
                    
                    <div className="mt-6 space-y-6">
                        <div>
                            <h4 className="font-bold mb-3 text-text-primary dark:text-dark-text-primary">1. Clear Section Separation</h4>
                            <div className="bg-bg-secondary dark:bg-dark-bg-secondary p-4 rounded-lg border border-border-color dark:border-dark-border-color">
                                <div className="text-sm font-mono text-text-secondary dark:text-dark-text-secondary">
                                    <div className="text-brand-orange">┌─ TABLE HEADER ──────────────────────────────────┐</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Title, Description</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Search, Filters, Controls</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Column Visibility, Actions</div>
                                    <div className="text-brand-orange">├─ TABLE BODY ───────────────────────────────────┤</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Column Headers</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Data Rows (Virtualized/Regular)</div>
                                    <div className="text-brand-orange">└─ TABLE FOOTER ──────────────────────────────────┘</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Results Info, Pagination</div>
                                    <div className="ml-4 text-text-primary dark:text-dark-text-primary">• Actions, Status, Metadata</div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-3 text-text-primary dark:text-dark-text-primary">2. Flexible Header Section</h4>
                            <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-3">
                                The header section can be customized or completely hidden. Perfect for adding custom controls, multiple buttons, or hiding when not needed.
                            </p>
                            <CodeBlock code={`// Custom header with multiple action buttons
<VirtualizedTable
    data={data}
    columns={columns}
    showHeader={true}
    headerContent={
        <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
                <h3 className="text-lg font-bold">Products</h3>
                <Badge variant="success">Active</Badge>
            </div>
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">Export</Button>
                <Button variant="outline" size="sm">Import</Button>
                <Button variant="primary" size="sm">Add Product</Button>
            </div>
        </div>
    }
/>`} />
                        </div>
                    </div>
                </SubSection>

                {/* Standard Table */}
                <SubSection title="Standard Table">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                        Use the standard Table component for simple data display with basic features like sorting, selection, and pagination.
                    </p>
                    
                    <div className="mt-6">
                        <ComponentPreview isColumn>
                            <Table striped hoverable>
                                <Table.Header>
                                    <Table.HeadCell>Product</Table.HeadCell>
                                    <Table.HeadCell>SKU</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Header>
                                <Table.Body>
                                    {simpleTableData.map(item => (
                                        <Table.Row key={item.id}>
                                            <Table.Cell>{item.name}</Table.Cell>
                                            <Table.Cell>{item.sku}</Table.Cell>
                                            <Table.Cell>
                                                <Badge variant={item.statusVariant}>{item.status}</Badge>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </ComponentPreview>
                    </div>
                </SubSection>

                {/* Virtualized Tables */}
                <SubSection title="Virtualized Tables">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                        VirtualizedTable is designed for large datasets (1000+ rows) with advanced features like virtualization, 
                        multi-column sorting, filtering, and search. Perfect for production applications with complex data requirements.
                    </p>
                    
                    <div className="mt-6">
                        <ComponentPreview isColumn>
                            <div className="w-full overflow-x-auto">
                                <VirtualizedTable
                                    data={largeTableData.slice(0, 50)}
                                    columns={virtualizedTableColumns}
                                    height={400}
                                    rowHeight={50}
                                    searchable={true}
                                    filterable={true}
                                    filters={[
                                        { key: 'category', label: 'All', value: 'all' },
                                        { key: 'category', label: 'Electronics', value: 'Electronics' },
                                        { key: 'category', label: 'Clothing', value: 'Clothing' },
                                        { key: 'category', label: 'Books', value: 'Books' },
                                        { key: 'category', label: 'Home', value: 'Home' },
                                        { key: 'category', label: 'Sports', value: 'Sports' }
                                    ]}
                                    onFilterChange={(_, value) => setActiveFilter(value)}
                                    activeFilter={activeFilter}
                                    selectable={true}
                                    striped={true}
                                    hoverable={true}
                                    title="Product Inventory"
                                    description="Large dataset with virtualization and advanced features. Try searching for 'xyz123' to see the empty state."
                                    showRowNumbers={true}
                                    multiSort={true}
                                    sortable={true}
                                />
                            </div>
                        </ComponentPreview>
                        
                        <div className="mt-4 p-4 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg border border-border-color dark:border-dark-border-color">
                            <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                                <strong>💡 Test the Empty State:</strong> In the table above, try searching for a term that doesn't exist (e.g., "xyz123", "nonexistent", or "test123"). 
                                The table should maintain its header and footer while showing a helpful empty state message in the body.
                            </p>
                        </div>
                    </div>
                </SubSection>

                {/* Interactive Workbench */}
                <SubSection title="Interactive Table Workbench">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                        Experiment with different table configurations and see the results in real-time. 
                        Switch between regular and virtualized tables to compare features.
                    </p>
                    
                    <div className="mt-6 space-y-6">
                        {/* Table Type Selection */}
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Table Type:</span>
                            <div className="flex gap-2">
                                <Button
                                    variant={tableType === 'regular' ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setTableType('regular')}
                                >
                                    Regular Table
                                </Button>
                                <Button
                                    variant={tableType === 'virtualized' ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setTableType('virtualized')}
                                >
                                    Virtualized Table
                                </Button>
                            </div>
                        </div>

                        {/* Table Controls */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Appearance</label>
                                <div className="space-y-2">
                                    <ToggleSwitch
                                        id="table-striped"
                                        label="Striped rows"
                                        checked={isStriped}
                                        onChange={(e) => setIsStriped(e.target.checked)}
                                    />
                                    <ToggleSwitch
                                        id="table-hoverable"
                                        label="Hover effects"
                                        checked={isHoverable}
                                        onChange={(e) => setIsHoverable(e.target.checked)}
                                    />
                                    <ToggleSwitch
                                        id="table-selectable"
                                        label="Row selection"
                                        checked={isSelectable}
                                        onChange={(e) => setIsSelectable(e.target.checked)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Features</label>
                                <div className="space-y-2">
                                    <ToggleSwitch
                                        id="table-searchable"
                                        label="Search"
                                        checked={isSearchable}
                                        onChange={(e) => setIsSearchable(e.target.checked)}
                                    />
                                    <ToggleSwitch
                                        id="table-filterable"
                                        label="Filters"
                                        checked={isFilterable}
                                        onChange={(e) => setIsFilterable(e.target.checked)}
                                    />
                                    <ToggleSwitch
                                        id="table-sortable"
                                        label="Sorting"
                                        checked={isSortable}
                                        onChange={(e) => setIsSortable(e.target.checked)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Advanced</label>
                                <div className="space-y-2">
                                    <ToggleSwitch
                                        id="table-multi-sort"
                                        label="Multi-column sort"
                                        checked={isMultiSort}
                                        onChange={(e) => setIsMultiSort(e.target.checked)}
                                    />
                                    <div className="space-y-1">
                                        <label className="text-xs text-text-secondary dark:text-dark-text-secondary">Default Sort</label>
                                        <select
                                            value={defaultSortPriority}
                                            onChange={(e) => setDefaultSortPriority(e.target.value)}
                                            className="w-full text-xs px-2 py-1 rounded border border-border-color dark:border-dark-border-color bg-bg-secondary dark:bg-dark-bg-secondary"
                                        >
                                            <option value="none">None</option>
                                            <option value="id">ID</option>
                                            <option value="customer">Customer</option>
                                            <option value="date">Date</option>
                                            <option value="amount">Amount</option>
                                            <option value="status">Status</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Highlighting</label>
                                <div className="space-y-2">
                                    <div className="space-y-1">
                                        <label className="text-xs text-text-secondary dark:text-dark-text-secondary">Highlight Color</label>
                                        <select
                                            value={highlightColor}
                                            onChange={(e) => setHighlightColor(e.target.value as any)}
                                            className="w-full text-xs px-2 py-1 rounded border border-border-color dark:border-dark-border-color bg-bg-secondary dark:bg-dark-bg-secondary"
                                        >
                                            <option value="primary">Primary (Orange)</option>
                                            <option value="secondary">Secondary (Navy)</option>
                                            <option value="success">Success (Green)</option>
                                            <option value="warning">Warning (Yellow)</option>
                                            <option value="error">Error (Red)</option>
                                            <option value="info">Info (Blue)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs text-text-secondary dark:text-dark-text-secondary">Highlighted Rows</label>
                                        <input
                                            type="text"
                                            value={highlightedRows.join(', ')}
                                            onChange={(e) => setHighlightedRows(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                            placeholder="inv-001, inv-002"
                                            className="w-full text-xs px-2 py-1 rounded border border-border-color dark:border-dark-border-color bg-bg-secondary dark:bg-dark-bg-secondary"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs text-text-secondary dark:text-dark-text-secondary">Highlighted Columns</label>
                                        <input
                                            type="text"
                                            value={highlightedColumns.join(', ')}
                                            onChange={(e) => setHighlightedColumns(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                            placeholder="status, amount"
                                            className="w-full text-xs px-2 py-1 rounded border border-border-color dark:border-dark-border-color bg-bg-secondary dark:bg-dark-bg-secondary"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Live Table Preview */}
                        <ComponentPreview isColumn>
                            {tableType === 'regular' ? (
                                <Card isInteractive={false} className="w-full">
                                    <Card.Body>
                                        <div className="w-full flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                                            <div className="flex items-center gap-4 w-full md:w-auto">
                                                <p className="font-bold text-lg text-text-primary dark:text-dark-text-primary flex-shrink-0">Invoices</p>
                                                {isSearchable && (
                                                    <div className="relative w-full md:w-64">
                                                        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary/60 pointer-events-none">search</Icon>
                                                        <input
                                                            type="text"
                                                            value={filterQuery}
                                                            onChange={(e) => setFilterQuery(e.target.value)}
                                                            placeholder="Filter invoices..."
                                                            className="w-full pl-10 pr-4 py-2 text-sm bg-bg-tertiary dark:bg-dark-bg-primary rounded-lg border border-transparent focus:outline-none focus:ring-2 focus:ring-brand-orange"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {isSelectable && selectedInvoices.length > 0 ? (
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-medium">{selectedInvoices.length} selected</span>
                                                    <ButtonGroup>
                                                        <Button variant="secondary" size="sm" leftIcon={<Icon>archive</Icon>}>Archive</Button>
                                                        <Button variant="secondary" size="sm" leftIcon={<Icon>delete</Icon>}>Delete</Button>
                                                    </ButtonGroup>
                                                </div>
                                            ) : (
                                                <Button variant="primary" size="sm" leftIcon={<Icon>add</Icon>}>New Invoice</Button>
                                            )}
                                        </div>
                                        
                                        {processedData.length > 0 ? (
                                            <Table striped={isStriped} hoverable={isHoverable}>
                                                <Table.Header>
                                                    {isSelectable && <Table.Checkbox
                                                        id="select-all-invoices"
                                                        label="Select all invoices on this page"
                                                        isHeader
                                                        checked={isPageAllSelected}
                                                        indeterminate={isPageSomeSelected}
                                                        onChange={handleSelectAll}
                                                    />}
                                                    <SortableHeader sortKey="id" sortConfigs={sortConfigs} onSort={requestSort} multiSort={isMultiSort}>Invoice</SortableHeader>
                                                    <SortableHeader sortKey="customer" sortConfigs={sortConfigs} onSort={requestSort} multiSort={isMultiSort}>Customer</SortableHeader>
                                                    <SortableHeader sortKey="date" sortConfigs={sortConfigs} onSort={requestSort} multiSort={isMultiSort}>Date</SortableHeader>
                                                    <SortableHeader sortKey="status" sortConfigs={sortConfigs} onSort={requestSort} multiSort={isMultiSort}>Status</SortableHeader>
                                                </Table.Header>
                                                <Table.Body>
                                                    {paginatedData.map((invoice) => (
                                                        <Table.Row 
                                                            key={invoice.id} 
                                                            selected={selectedInvoices.includes(invoice.id)}
                                                            highlighted={highlightedRows.includes(invoice.id)}
                                                            highlightColor={highlightColor}
                                                        >
                                                            {isSelectable && <Table.Checkbox
                                                                id={`select-${invoice.id}`}
                                                                label={`Select invoice ${invoice.id}`}
                                                                checked={selectedInvoices.includes(invoice.id)}
                                                                onChange={(e) => handleSelectOne(e, invoice.id)}
                                                            />}
                                                            <Table.Cell 
                                                                highlighted={highlightedColumns.includes('id')}
                                                                highlightColor={highlightColor}
                                                            >
                                                                <span className="font-bold font-mono text-text-primary dark:text-dark-text-primary">{invoice.id}</span>
                                                            </Table.Cell>
                                                            <Table.Cell 
                                                                highlighted={highlightedColumns.includes('customer')}
                                                                highlightColor={highlightColor}
                                                            >
                                                                {invoice.customer}
                                                            </Table.Cell>
                                                            <Table.Cell 
                                                                highlighted={highlightedColumns.includes('date')}
                                                                highlightColor={highlightColor}
                                                            >
                                                                {invoice.date}
                                                            </Table.Cell>
                                                            <Table.Cell 
                                                                highlighted={highlightedColumns.includes('status')}
                                                                highlightColor={highlightColor}
                                                            >
                                                                <Badge variant={invoice.statusVariant}>{invoice.status}</Badge>
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    ))}
                                                </Table.Body>
                                            </Table>
                                        ) : (
                                            <div className="py-12">
                                                <EmptyState
                                                    icon="search_off"
                                                    title="No Invoices Found"
                                                    description="Your search did not match any invoices. Try adjusting your filter."
                                                />
                                            </div>
                                        )}
                                    </Card.Body>
                                    {totalPages > 1 && processedData.length > 0 && (
                                        <Card.Footer className="justify-between">
                                            <div className="flex items-center gap-4">
                                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">
                                                    Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, processedData.length)} of {processedData.length} results
                                                </span>
                                                {sortConfigs.length > 0 && (
                                                    <button
                                                        onClick={() => setSortConfigs([])}
                                                        className="text-xs text-brand-orange hover:text-brand-orange/80 transition-colors"
                                                    >
                                                        Clear sorting
                                                    </button>
                                                )}
                                            </div>
                                            <Pagination 
                                                currentPage={currentPage} 
                                                totalPages={totalPages} 
                                                onPageChange={setCurrentPage} 
                                            />
                                        </Card.Footer>
                                    )}
                                </Card>
                            ) : (
                                <Card isInteractive={false} className="w-full">
                                    <Card.Body>
                                        <div className="w-full overflow-x-auto">
                                            <VirtualizedTable
                                                data={largeTableData}
                                                columns={virtualizedTableColumns}
                                                height={tableHeight}
                                                rowHeight={rowHeight}
                                                searchable={isSearchable}
                                                filterable={isFilterable}
                                                filters={[
                                                    { key: 'category', label: 'All', value: 'all' },
                                                    { key: 'category', label: 'Electronics', value: 'Electronics' },
                                                    { key: 'category', label: 'Clothing', value: 'Clothing' },
                                                    { key: 'category', label: 'Books', value: 'Books' },
                                                    { key: 'category', label: 'Home', value: 'Home' },
                                                    { key: 'category', label: 'Sports', value: 'Sports' }
                                                ]}
                                                onFilterChange={(_, value) => setActiveFilter(value)}
                                                activeFilter={activeFilter}
                                                selectable={isSelectable}
                                                striped={isStriped}
                                                hoverable={isHoverable}
                                                title="Product Inventory"
                                                description="Large dataset with virtualization and advanced features"
                                                showRowNumbers={true}
                                                multiSort={isMultiSort}
                                                sortable={isSortable}
                                                defaultSort={defaultSortPriority !== 'none' ? [{ key: defaultSortPriority as any, direction: 'asc', priority: 1 }] : []}
                                                highlightedRows={highlightedRows}
                                                highlightedColumns={highlightedColumns}
                                                highlightColor={highlightColor}
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </ComponentPreview>
                    </div>
                </SubSection>

                {/* Highlighting Examples */}
                <SubSection title="Row & Column Highlighting">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                        Use highlighting to draw attention to specific rows or columns. Perfect for emphasizing important data, 
                        highlighting errors, or showing status changes.
                    </p>
                    
                    <div className="mt-6 space-y-6">
                        <div>
                            <h4 className="font-bold mb-3 text-text-primary dark:text-dark-text-primary">Row Highlighting</h4>
                            <div className="bg-bg-secondary dark:bg-dark-bg-secondary p-4 rounded-lg border border-border-color dark:border-dark-border-color">
                                <Table striped hoverable>
                                    <Table.Header>
                                        <Table.HeadCell>ID</Table.HeadCell>
                                        <Table.HeadCell>Customer</Table.HeadCell>
                                        <Table.HeadCell>Status</Table.HeadCell>
                                        <Table.HeadCell>Amount</Table.HeadCell>
                                    </Table.Header>
                                    <Table.Body>
                                        <Table.Row highlighted highlightColor="success">
                                            <Table.Cell>INV-001</Table.Cell>
                                            <Table.Cell>Acme Corp</Table.Cell>
                                            <Table.Cell>Paid</Table.Cell>
                                            <Table.Cell>$2,500</Table.Cell>
                                        </Table.Row>
                                        <Table.Row highlighted highlightColor="warning">
                                            <Table.Cell>INV-002</Table.Cell>
                                            <Table.Cell>Tech Solutions</Table.Cell>
                                            <Table.Cell>Pending</Table.Cell>
                                            <Table.Cell>$1,800</Table.Cell>
                                        </Table.Row>
                                        <Table.Row highlighted highlightColor="error">
                                            <Table.Cell>INV-003</Table.Cell>
                                            <Table.Cell>Global Inc</Table.Cell>
                                            <Table.Cell>Overdue</Table.Cell>
                                            <Table.Cell>$3,200</Table.Cell>
                                        </Table.Row>
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-3 text-text-primary dark:text-dark-text-primary">Column Highlighting</h4>
                            <div className="bg-bg-secondary dark:bg-dark-bg-secondary p-4 rounded-lg border border-border-color dark:border-dark-border-color">
                                <Table striped hoverable>
                                    <Table.Header>
                                        <Table.HeadCell>ID</Table.HeadCell>
                                        <Table.HeadCell>Customer</Table.HeadCell>
                                        <Table.HeadCell>Status</Table.HeadCell>
                                        <Table.HeadCell>Amount</Table.HeadCell>
                                    </Table.Header>
                                    <Table.Body>
                                        <Table.Row>
                                            <Table.Cell highlighted highlightColor="primary">INV-001</Table.Cell>
                                            <Table.Cell>Acme Corp</Table.Cell>
                                            <Table.Cell highlighted highlightColor="success">Paid</Table.Cell>
                                            <Table.Cell highlighted highlightColor="info">$2,500</Table.Cell>
                                        </Table.Row>
                                        <Table.Row>
                                            <Table.Cell highlighted highlightColor="primary">INV-002</Table.Cell>
                                            <Table.Cell>Tech Solutions</Table.Cell>
                                            <Table.Cell highlighted highlightColor="warning">Pending</Table.Cell>
                                            <Table.Cell highlighted highlightColor="info">$1,800</Table.Cell>
                                        </Table.Row>
                                        <Table.Row>
                                            <Table.Cell highlighted highlightColor="primary">INV-003</Table.Cell>
                                            <Table.Cell>Global Inc</Table.Cell>
                                            <Table.Cell highlighted highlightColor="error">Overdue</Table.Cell>
                                            <Table.Cell highlighted highlightColor="info">$3,200</Table.Cell>
                                        </Table.Row>
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-3 text-text-primary dark:text-dark-text-primary">Code Example</h4>
                            <CodeBlock code={`// Row highlighting
<Table.Row highlighted highlightColor="success">
    <Table.Cell>Data</Table.Cell>
</Table.Row>

// Cell highlighting  
<Table.Cell highlighted highlightColor="warning">
    Important data
</Table.Cell>

// VirtualizedTable highlighting
<VirtualizedTable
    highlightedRows={['row-1', 'row-3']}
    highlightedColumns={['status', 'amount']}
    highlightColor="primary"
    // ... other props
/>`} />
                        </div>
                    </div>
                </SubSection>

                {/* Overlay Fix Note */}
                <SubSection title="Important: Overlay Rendering">
                    <div className="bg-brand-orange/10 dark:bg-dark-brand-orange/10 border border-brand-orange/20 dark:border-dark-brand-orange/20 rounded-lg p-4 mb-6">
                        <h4 className="font-bold text-brand-orange dark:text-dark-brand-orange mb-2">✅ Overlay Clipping Fixed!</h4>
                        <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                            We've fixed the issue where dropdowns, date pickers, and other overlays were being clipped by demo containers. 
                            All overlays now render properly above their parent containers with proper z-index management.
                        </p>
                    </div>
                </SubSection>

                {/* Component Props */}
                <SubSection title="Props: <Table />">
                    <BrowserCompatibility>
                        <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                           The <code>{'<Table>'}</code> component provides a flexible foundation for displaying tabular data with basic features like sorting, selection, and pagination.
                        </p>
                        <div className="component-preview !p-0 !bg-transparent !border-none mt-4">
                            <PropsTable data={tableRootProps} />
                        </div>
                    </BrowserCompatibility>
                </SubSection>

                <SubSection title="Props: <Table.Row />">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                       Individual table rows can be highlighted and styled with various color themes for better visual emphasis.
                    </p>
                    <div className="component-preview !p-0 !bg-transparent !border-none mt-4">
                        <PropsTable data={tableRowProps} />
                    </div>
                </SubSection>

                <SubSection title="Props: <Table.Cell />">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                       Individual table cells can be highlighted independently with various color themes for precise data emphasis.
                    </p>
                    <div className="component-preview !p-0 !bg-transparent !border-none mt-4">
                        <PropsTable data={tableCellProps} />
                    </div>
                </SubSection>

                <SubSection title="Props: <VirtualizedTable />">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                       The <code>{'<VirtualizedTable>'}</code> component is designed for large datasets and provides advanced features like virtualization, sorting, filtering, and search.
                    </p>
                    <div className="component-preview !p-0 !bg-transparent !border-none mt-4">
                        <PropsTable data={virtualizedTableProps} />
                    </div>
                </SubSection>

                {/* Column Configuration */}
                <SubSection title="Column Configuration">
                    <p className="text-text-secondary dark:text-dark-text-secondary mt-6 max-w-2xl">
                       Each column in the VirtualizedTable can be configured with various options including text wrapping behavior and custom styling:
                    </p>
                    <div className="component-preview !p-0 !bg-transparent !border-none mt-4">
                        <PropsTable data={columnConfigProps} />
                    </div>
                </SubSection>
            </div>
        </SectionContainer>
    );
};
